import React, { useState, useRef, useEffect, memo } from 'react';
import { FaChevronDown, FaLayerGroup, FaInfoCircle } from 'react-icons/fa';
import { layerGroups } from '../config/layers';
import './LayerControl.css';

const LayerControl = ({
    onToggleLayer,
    isAuthenticated,
    setShowAuthDialog,
    isLayerLoading
}) => {
    const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
    const [selectedSectionId, setSelectedSectionId] = useState("counties");
    const [checkedLayers, setCheckedLayers] = useState(new Set(['counties']));
    const [expandedSections, setExpandedSections] = useState({
        sources: false,
        rbfs: false,
        ms2: false,
        counties: true
    });
    const layerMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (layerMenuRef.current && !layerMenuRef.current.contains(event.target)) {
                setIsLayerMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleLayerToggle = (layer, isChecked) => {
        if (!isAuthenticated) {
            setShowAuthDialog(true);
            return;
        }

        setSelectedSectionId(layer.id);
        setCheckedLayers(prev => {
            const newCheckedLayers = new Set();
            if (isChecked) {
                newCheckedLayers.add(layer.id);
            }
            return newCheckedLayers;
        });
        onToggleLayer(layer, isChecked);
    };

    const renderLayerGroup = (groupId) => {
        const group = layerGroups[groupId];
        if (!group) return null;

        return (
            <div className="layer-parent-section">
                <div
                    className="layer-parent-header"
                    onClick={() => toggleSection(groupId)}
                >
                    <div className="layer-parent-title-wrapper">
                        <span className="layer-parent-title">
                            {group.title}
                            {group.tooltip && (
                                <div className="layer-help-tooltip" title={group.tooltip}>
                                    <FaInfoCircle />
                                </div>
                            )}
                        </span>
                    </div>
                    <FaChevronDown className={`accordion-icon ${expandedSections[groupId] ? 'expanded' : ''}`} />
                </div>
                <div className={`layer-parent-content ${expandedSections[groupId] ? 'expanded' : ''}`}>
                    {group.sections ? (
                        group.sections.map(sectionId => (
                            <div key={sectionId} className="layer-subsection">
                                <div
                                    className="layer-subsection-header"
                                    onClick={() => toggleSection(sectionId)}
                                >
                                    <div className="layer-parent-title-wrapper">
                                        <span className="layer-subsection-title">
                                            {layerGroups[sectionId].title}
                                        </span>
                                    </div>
                                    <FaChevronDown className={`accordion-icon ${expandedSections[sectionId] ? 'expanded' : ''}`} />
                                </div>
                                <div className={`layer-subsection-content ${expandedSections[sectionId] ? 'expanded' : ''}`}>
                                    <div className="layer-section-content">
                                        {layerGroups[sectionId].layers.map(layer => (
                                            <div key={layer.id} className="layer-option">
                                                <input
                                                    type="checkbox"
                                                    id={`${sectionId}-${layer.id}`}
                                                    checked={checkedLayers.has(layer.id)}
                                                    disabled={isLayerLoading}
                                                    onChange={(e) => handleLayerToggle(layer, e.target.checked)}
                                                />
                                                <label
                                                    htmlFor={`${sectionId}-${layer.id}`}
                                                    className="layer-option-text"
                                                >
                                                    {layer.title}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="layer-section-content">
                            {group.layers.map(layer => (
                                <div key={layer.id} className="layer-option">
                                    <input
                                        type="checkbox"
                                        id={`${groupId}-${layer.id}`}
                                        checked={checkedLayers.has(layer.id)}
                                        disabled={isLayerLoading}
                                        onChange={(e) => handleLayerToggle(layer, e.target.checked)}
                                    />
                                    <label
                                        htmlFor={`${groupId}-${layer.id}`}
                                        className="layer-option-text"
                                    >
                                        {layer.title}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLayerMenuOpen(!isLayerMenuOpen);
    };

    return (
        <div className="layer-control" ref={layerMenuRef}>
            <button
                className="layer-control-button"
                onClick={handleButtonClick}
                aria-label="Toggle layer menu"
                type="button"
            >
                <FaLayerGroup size={20} />
            </button>
            {isLayerMenuOpen && (
                <div className="layer-menu">
                    {renderLayerGroup('sources')}
                    {renderLayerGroup('counties')}
                </div>
            )}
        </div>
    );
};

LayerControl.displayName = 'LayerControl';

export default memo(LayerControl);